import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { fmtDate, fmtMoney } from "../lib/format";
import { AlertTriangle, Flame, Clock, ArrowRight, Plus } from "lucide-react";

const PRIORITY_TONE = { low: "pm-pill-slate", medium: "pm-pill-blue", high: "pm-pill-amber", urgent: "pm-pill-red" };
const STATUS_TONE = { open: "pm-pill-green", pending: "pm-pill-amber", resolved: "pm-pill-slate" };
const STAGE_INDEX = ["interest", "offer", "negotiation", "agreed", "closed"];
const STAGE_LABEL = { interest: "Interest", offer: "Offer tabled", negotiation: "Negotiation", agreed: "Agreed", collapsed: "Collapsed", closed: "Closed" };
const STAGE_TONE = { interest: "bg-slate-200", offer: "bg-blue-500", negotiation: "bg-amber-500", agreed: "bg-emerald-500", collapsed: "bg-red-500", closed: "bg-emerald-700" };

const FILTERS = [
  { id: "all", label: "All" }, { id: "urgent", label: "Urgent" },
  { id: "sla_breach", label: "SLA breach" }, { id: "open", label: "Open" },
  { id: "pending", label: "Pending" }, { id: "resolved", label: "Resolved" },
];

const VIEW_TABS = [
  { id: "inbox", label: "Inbox" },
  { id: "transfer", label: "Transfer window tracker" },
];

export default function CasesInbox() {
  const [searchParams] = useSearchParams();
  const [view, setView] = useState("inbox");
  return (
    <Layout title="Cases" subtitle="CRM threads · urgency-scored">
      <div className="border-b border-slate-200 flex items-center gap-1 mb-6">
        {VIEW_TABS.map((t) => (
          <button key={t.id} onClick={() => setView(t.id)} data-testid={`cases-view-${t.id}`}
            className={`px-4 py-3 text-sm font-bold tracking-tight border-b-2 transition-all ${
              view === t.id ? "border-emerald-500 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"
            }`}>{t.label}</button>
        ))}
      </div>
      {view === "inbox" && <InboxView initialFilter={searchParams.get("filter") || "all"} />}
      {view === "transfer" && <TransferTracker />}
    </Layout>
  );
}

function InboxView({ initialFilter }) {
  const [cases, setCases] = useState([]);
  const [filter, setFilter] = useState(initialFilter || "all");
  const [q, setQ] = useState("");

  useEffect(() => { api.get("/v2/cases").then((r) => setCases(r.data || [])); }, []);

  const filtered = useMemo(() => {
    let list = cases;
    if (filter === "urgent") list = list.filter((c) => c.priority === "urgent");
    else if (filter === "sla_breach") list = list.filter((c) => c.sla_breached);
    else if (filter !== "all") list = list.filter((c) => c.status === filter);
    if (q) {
      const s = q.toLowerCase();
      list = list.filter((c) =>
        c.title.toLowerCase().includes(s) || (c.client_name || "").toLowerCase().includes(s) ||
        (c.parties || []).join(" ").toLowerCase().includes(s),
      );
    }
    return list;
  }, [cases, filter, q]);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-xl p-1">
          {FILTERS.map((f) => (
            <button key={f.id} onClick={() => setFilter(f.id)} data-testid={`cases-filter-${f.id}`}
              className={`px-3 h-8 rounded-lg text-xs font-bold transition-all ${
                filter === f.id ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
              }`}>{f.label}</button>
          ))}
        </div>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search cases…"
               className="pm-input max-w-sm" data-testid="cases-search-input" />
        <div className="text-xs text-slate-500 font-mono-tab">{filtered.length} cases · sorted by urgency</div>
        <div className="flex-1" />
        <Link
          to="/clients"
          className="pm-btn-primary h-10 px-4 rounded-xl text-sm font-bold flex items-center gap-1.5"
          data-testid="cases-new-case-btn"
          title="Open a client profile to start a new case from their Cases tab"
        >
          <Plus size={13} /> New case
        </Link>
      </div>

      <div className="space-y-2">
        {filtered.map((c) => (
          <Link key={c.id} to={`/cases/${c.id}`} data-testid={`inbox-case-${c.id}`}
                className="pm-card pm-card-hover p-4 flex items-center gap-4">
            {c.client_photo && <img src={c.client_photo} alt="" className="w-12 h-12 rounded-xl object-cover" />}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`pm-pill ${PRIORITY_TONE[c.priority]}`}>{c.priority}</span>
                <span className={`pm-pill ${STATUS_TONE[c.status]}`}>{c.status}</span>
                <span className="pm-pill pm-pill-slate">{c.case_type.replace("_", " ")}</span>
                {c.sla_breached && (
                  <span className="pm-pill pm-pill-red flex items-center gap-1"><Clock size={10} /> SLA breach</span>
                )}
                <span className="pm-pill bg-emerald-100 text-emerald-800 flex items-center gap-1">
                  <Flame size={10} /> {c.urgency_score}
                </span>
              </div>
              <div className="font-bold text-slate-900 mt-1.5 truncate">{c.title}</div>
              <div className="text-xs text-slate-500 truncate mt-0.5">
                {c.client_name} · {c.client_club} · {(c.parties || []).join(" · ")}
              </div>
            </div>
            <div className="text-xs text-slate-400 font-mono-tab whitespace-nowrap">{fmtDate(c.updated_at)}</div>
          </Link>
        ))}
        {filtered.length === 0 && <div className="text-sm text-slate-500">No cases match these filters.</div>}
      </div>
    </div>
  );
}

function TransferTracker() {
  const [deals, setDeals] = useState([]);
  useEffect(() => { api.get("/transfer-deals").then((r) => setDeals(r.data || [])); }, []);

  const updateStage = async (id, stage) => {
    await api.patch(`/transfer-deals/${id}`, { stage });
    const { data } = await api.get("/transfer-deals");
    setDeals(data || []);
  };

  // Group by window
  const summer = deals.filter((d) => d.window === "summer");
  const winter = deals.filter((d) => d.window === "winter");

  return (
    <div className="space-y-8" data-testid="transfer-tracker">
      <WindowSection title="Summer window" deals={summer} updateStage={updateStage} />
      <WindowSection title="Winter window" deals={winter} updateStage={updateStage} />
    </div>
  );
}

function WindowSection({ title, deals, updateStage }) {
  if (deals.length === 0) return null;
  const closest = deals.reduce((a, b) => (new Date(a.deadline) < new Date(b.deadline) ? a : b), deals[0]);
  const daysLeft = Math.ceil((new Date(closest.deadline) - new Date()) / (1000 * 60 * 60 * 24));
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display text-xl font-bold tracking-tight">{title}</h3>
        <div className={`pm-pill ${daysLeft <= 14 ? "pm-pill-red" : daysLeft <= 30 ? "pm-pill-amber" : "pm-pill-blue"}`}>
          Window closes in {daysLeft}d · {fmtDate(closest.deadline)}
        </div>
      </div>
      <div className="space-y-3">
        {deals.map((d) => (
          <div key={d.id} className="pm-card p-5" data-testid={`deal-${d.id}`}>
            <div className="flex items-center gap-4 mb-3">
              {d.client_photo && <img src={d.client_photo} alt="" className="w-10 h-10 rounded-lg object-cover" />}
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-base truncate">
                  {d.client_name} <span className="text-slate-400 font-medium">→</span> {d.interested_club}
                </div>
                <div className="text-xs text-slate-500">{d.client_from_club} · {d.window} window · {d.fee_eur_m ? `€${d.fee_eur_m}M` : "Fee TBD"}</div>
              </div>
            </div>
            {/* Stage pipeline */}
            <div className="flex items-center gap-1">
              {STAGE_INDEX.map((s, i) => {
                const reached = STAGE_INDEX.indexOf(d.stage) >= i;
                const active = d.stage === s;
                return (
                  <button key={s} onClick={() => updateStage(d.id, s)}
                          data-testid={`stage-${d.id}-${s}`}
                          className={`flex-1 py-2 rounded-md text-[10px] font-bold uppercase tracking-widest transition-all
                            ${active ? `${STAGE_TONE[s]} text-white` : reached ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-slate-50 text-slate-400 border border-slate-200 hover:bg-slate-100"}`}>
                    {STAGE_LABEL[s]}
                  </button>
                );
              })}
            </div>
            {d.notes && <div className="text-xs text-slate-600 mt-3">{d.notes}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}
